/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Help Page - Central help hub with support resources, legal links, and app info
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  HelpCircle,
  MessageSquare,
  Shield,
  BookOpen,
  FileText,
  Scale,
  Info,
  ExternalLink
} from 'lucide-react'
import { useBranding } from '../../stores/brandingStore'
import VersionInfo from '../../components/VersionInfo'

const Help = () => {
  const branding = useBranding()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const currentYear = new Date().getFullYear()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Link>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <HelpCircle className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Help & Support
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Resources, support, and information about {branding.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Support Resources */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Support Resources
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/help/getting-started"
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
            >
              <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Getting Started</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn the basics of {branding.name}
              </p>
            </Link>

            <Link
              to="/contact"
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
            >
              <MessageSquare className="w-8 h-8 text-green-600 dark:text-green-400 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Contact Support</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Get help or send feedback</p>
            </Link>

            <Link
              to="/privacy-request"
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
            >
              <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Privacy Requests</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Data access, deletion, or corrections
              </p>
            </Link>
          </div>
        </div>

        {/* Legal Links */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Legal & Privacy
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/legal/terms"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Terms of Service</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Usage terms and conditions
                </p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
            </Link>

            <Link
              to="/legal/privacy"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Shield className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Privacy Policy</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">How we handle your data</p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
            </Link>

            <Link
              to="/privacy-request"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Scale className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">CCPA / GDPR Rights</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Exercise your data rights
                </p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
            </Link>

            <Link
              to="/profile#privacy"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Shield className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Privacy Settings</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage your privacy preferences
                </p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
            </Link>
          </div>
        </div>

        {/* App Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              About {branding.name}
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Application</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {branding.name} Adaptive Learning
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Version</span>
              <VersionInfo />
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Developer</span>
              <span className="font-medium text-gray-900 dark:text-white">{branding.company}</span>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-gray-600 dark:text-gray-400">Copyright</span>
              <span className="text-gray-500 dark:text-gray-400">
                © {currentYear} {branding.company}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Help
